import React, { useContext, useEffect, useState } from "react";
import {
  Calendar,
  ChevronDown,
  FileText,
  Filter,
  User,
  Wallet,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import Pagination from "../components/Pagination";
import { CareGiverContext } from "../context/CareGiverContext";
import { AppContext } from "../context/AppContext";
import NotOnboardedMessage from "../components/NotOnboardedMessage";
import { caregiverNetAmount } from "../utils/paymentDisplay";

const STATUS_OPTIONS = ["all", "ACTIVE", "PAUSED", "COMPLETED", "TERMINATED"];

const getStatusClass = (status) => {
  switch (status) {
    case "ACTIVE":
      return "bg-emerald-600 text-white";
    case "PAUSED":
      return "bg-yellow-500 text-white";
    case "COMPLETED":
      return "bg-blue-600 text-white";
    case "TERMINATED":
      return "bg-red-600 text-white";
    default:
      return "bg-gray-600 text-white";
  }
};

const CareGiverContracts = () => {
  const { t } = useTranslation();
  const { currency } = useContext(AppContext);
  const {
    cToken,
    contracts,
    contractPagination,
    fetchContracts,
    loading,
    notOnboarded,
    profileData,
  } = useContext(CareGiverContext);

  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [pagination, setPagination] = useState({ page: 1, limit: 8 });
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  const getTranslatedStatus = (status) => {
    switch (status) {
      case "ACTIVE":
        return t("contracts.active");
      case "PAUSED":
        return t("contracts.paused");
      case "COMPLETED":
        return t("contracts.completed");
      case "TERMINATED":
        return t("contracts.terminated");
      default:
        return status;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setShowStatusDropdown(false);
      }
    };

    if (showStatusDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showStatusDropdown]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setShowFilters(true);
      } else {
        setShowFilters(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!cToken) return;
    if (!profileData) return;
    if (notOnboarded) return;
    fetchContracts({
      page: pagination.page,
      limit: pagination.limit,
      status: statusFilter,
    });
  }, [
    cToken,
    profileData,
    notOnboarded,
    fetchContracts,
    pagination.page,
    pagination.limit,
    statusFilter,
  ]);

  // Show spinner while profile is loading
  if (!profileData) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-gray-500 dark:text-dark-muted">
          {t("contracts.loading")}
        </p>
      </div>
    );
  }

  if (notOnboarded) {
    return <NotOnboardedMessage featureName="contracts" />;
  }

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleItemsPerPageChange = (limit) => {
    setPagination({ page: 1, limit: Number(limit) });
  };

  const resetFilters = () => {
    setStatusFilter("all");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 relative">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text dark:text-dark-text">
            {t("contracts.title")}
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1 text-sm">
            {t("contracts.subtitle")}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {t("contracts.showingOf", {
              count: contracts.length,
              total: contractPagination.total,
            })}
          </p>
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-card dark:bg-dark-surface rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors w-fit text-sm font-semibold"
        >
          <Filter size={18} className="text-primary" />
          {showFilters
            ? t("contracts.hideFilters")
            : t("contracts.showFilters")}
        </button>
      </div>

      {showFilters && (
        <div className="bg-card dark:bg-dark-surface p-4 rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative min-w-[220px] dropdown-container">
              <button
                type="button"
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none text-text dark:text-dark-text font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <span>
                  {statusFilter === "all"
                    ? t("contracts.allStatuses")
                    : getTranslatedStatus(statusFilter)}
                </span>
                <ChevronDown
                  size={16}
                  className={`text-primary transition-transform ${
                    showStatusDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showStatusDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
                  {STATUS_OPTIONS.map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        handleStatusChange(status);
                        setShowStatusDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        statusFilter === status
                          ? "bg-primary text-white dark:bg-primary"
                          : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                      }`}
                    >
                      {status === "all"
                        ? t("contracts.allStatuses")
                        : getTranslatedStatus(status)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={resetFilters}
              className="px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 text-text dark:text-dark-text font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
            >
              {t("contracts.resetAll")}
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-500 dark:text-dark-muted">
            {t("contracts.loading")}
          </p>
        </div>
      )}

      {!loading && (
        <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border overflow-hidden">
          <div className="overflow-x-auto -mx-4 md:mx-0">
            <div className="min-w-[820px] md:min-w-full">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700">
                    <th className="px-4 md:px-6 py-5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {t("contracts.client")}
                    </th>
                    <th className="px-4 md:px-6 py-5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {t("contracts.colSource") || "Source"}
                    </th>
                    <th className="px-4 md:px-6 py-5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {t("contracts.salary")}
                    </th>
                    <th className="px-4 md:px-6 py-5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {t("contracts.colTimeline") || "Timeline"}
                    </th>
                    <th className="px-4 md:px-6 py-5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {t("contracts.status")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {contracts.length > 0 ? (
                    contracts.map((contract) => (
                      <tr
                        key={contract.id}
                        className="group hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        <td className="px-4 md:px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-primary">
                              <User size={18} />
                            </div>
                            <div>
                              <div className="font-medium text-text dark:text-dark-text text-sm">
                                {contract.client?.name || "N/A"}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-dark-muted">
                                {contract.client?.phoneNumber ||
                                  contract.client?.email ||
                                  "No contact"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 md:px-6 py-4">
                          <div className="flex items-center gap-2 text-sm text-text dark:text-dark-text">
                            <FileText size={16} className="text-primary" />
                            {contract.bookingId
                              ? t("contracts.sourceBooking") || "Booking"
                              : t("contracts.sourceJobPost") || "Job Post"}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-dark-muted mt-1">
                            {contract.jobPost?.description ||
                              contract.booking?.status ||
                              "Contract work"}
                          </div>
                        </td>
                        <td className="px-4 md:px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Wallet size={16} className="text-emerald-500" />
                            <span className="font-bold text-text dark:text-dark-text">
                              {currency}{" "}
                              {caregiverNetAmount(
                                contract.monthlySalary,
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-dark-muted mt-1">
                            {t("contracts.monthlyEarnings") ||
                              "Your monthly earnings"}
                          </div>
                        </td>
                        <td className="px-4 md:px-6 py-4">
                          <div className="space-y-1 text-sm text-text dark:text-dark-text">
                            <div className="flex items-center gap-2">
                              <Calendar size={14} className="text-gray-400" />
                              {contract.startDate
                                ? new Date(
                                    contract.startDate,
                                  ).toLocaleDateString()
                                : "N/A"}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-dark-muted">
                              {t("contracts.endsLabel") || "Ends:"}{" "}
                              {contract.endDate
                                ? new Date(
                                    contract.endDate,
                                  ).toLocaleDateString()
                                : t("contracts.endsOpen") || "Open"}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 md:px-6 py-4">
                          <span
                            className={`px-3 py-1 rounded-lg text-xs font-bold ${getStatusClass(
                              contract.status,
                            )}`}
                          >
                            {getTranslatedStatus(contract.status)}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 md:px-6 py-12 text-center text-gray-500 dark:text-dark-muted"
                      >
                        {t("contracts.noContracts")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="border-t border-gray-100 dark:border-dark-border bg-gray-50/50 dark:bg-dark-surface p-2">
            <Pagination
              currentPage={pagination.page}
              totalPages={contractPagination.totalPages}
              onPageChange={(page) =>
                setPagination((prev) => ({ ...prev, page }))
              }
              itemsPerPage={pagination.limit}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CareGiverContracts;
